import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Loader2, ExternalLink, ShieldCheck } from 'lucide-react';
import { AuthShell, AuthErrorBanner } from '@/components/auth/AuthShell';
import { useAuth } from '@/contexts/AuthContext';
import { errorText } from '@/lib/permissionError';

/**
 * F3 - Post-MFA T&C acceptance screen.
 *
 * Reached only via a `step='terms_acceptance_required'` challenge from
 * loginMfa. On Accept, POSTs to /auth/accept-terms; the server records
 * the acceptance and mints the session JWT.
 *
 * The Cancel button ends the login attempt (clears the challenge) and
 * returns to /login. There is no "skip" - the gate is strict.
 */
export function AcceptTermsPage() {
  const navigate = useNavigate();
  const { pendingChallenge, acceptTerms, cancelChallenge } = useAuth();
  const [agreed, setAgreed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (!pendingChallenge || pendingChallenge.step !== 'terms_acceptance_required') {
    // Someone landed here without an active T&C challenge; punt to /login.
    return <Navigate to="/login" replace />;
  }

  const termsVersion = pendingChallenge.termsVersion ?? 'current';

  async function handleAccept() {
    setError(null);
    setSubmitting(true);
    try {
      await acceptTerms();
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(errorText(err, 'Could not record acceptance. Please try again.'));
      setSubmitting(false);
    }
  }

  function handleCancel() {
    cancelChallenge();
    navigate('/login', { replace: true });
  }

  return (
    <AuthShell
      title="Terms & Conditions"
      lede="Read and accept the DreamTrials Terms & Conditions to continue."
    >
      <div className="bg-white rounded-md shadow-card border border-border-soft p-6 relative">
        <p className="eyebrow mb-2 flex items-center gap-1.5">
          <ShieldCheck size={12} className="text-navy" />
          First-time sign-in
        </p>
        <h2 className="font-bold text-lg text-navy mb-1">Before you continue</h2>
        <p className="text-[13px] text-text-3 mb-4">
          DreamTrials requires every user to review and accept the Terms &amp; Conditions
          before entering the platform. This is a one-time step; you will only be asked
          again if the terms are materially revised.
        </p>

        <div className="rounded-sm bg-app border border-border-strong p-3 mb-4">
          <p className="text-[12px] font-semibold text-text-2 mb-1">
            Current version: <span className="font-mono">{termsVersion}</span>
          </p>
          <a
            href="/legal/terms"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-[12px] font-bold text-blue hover:text-navy underline underline-offset-2"
          >
            Open Terms &amp; Conditions in a new tab <ExternalLink size={11} />
          </a>
          <div className="flex gap-3 mt-2 text-[11.5px] text-text-3">
            <a href="/legal/privacy" target="_blank" rel="noopener noreferrer" className="hover:text-navy underline">
              Privacy Policy
            </a>
            <span aria-hidden>·</span>
            <a href="/legal/cookies" target="_blank" rel="noopener noreferrer" className="hover:text-navy underline">
              Cookies
            </a>
          </div>
        </div>

        <label className="flex items-start gap-2 text-[13px] text-text-2 cursor-pointer select-none mb-4">
          <input
            type="checkbox"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            data-testid="terms-agree-checkbox"
            className="mt-0.5 h-4 w-4 accent-navy"
          />
          <span>
            I have read and agree to the DreamTrials <strong>Terms &amp; Conditions</strong>,
            <strong> Privacy Policy</strong>, and <strong>Cookies Policy</strong>.
          </span>
        </label>

        {error && (
          <div className="mb-3">
            <AuthErrorBanner message={error} />
          </div>
        )}

        <div className="flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={handleCancel}
            disabled={submitting}
            className="h-11 px-3 rounded-sm text-[13px] text-text-2 hover:bg-soft"
          >
            Cancel and sign out
          </button>
          <button
            type="button"
            onClick={handleAccept}
            disabled={!agreed || submitting}
            data-testid="terms-accept-submit"
            className="h-11 px-4 rounded-sm bg-navy hover:bg-navy-600 text-white font-bold text-[13px] flex items-center gap-1.5 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {submitting && <Loader2 size={14} className="animate-spin" />}
            {submitting ? 'Signing in…' : 'I Agree — Continue'}
          </button>
        </div>
      </div>
    </AuthShell>
  );
}
